import { Alert, Link } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { IconClickEvent } from '@mm/icons';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel, {
  EmailWidgetConfig,
  NotificationWidgetKind,
  WidgetBase,
} from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import classnames from 'classnames';
import React, { useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { WidgetContainerRefProp } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { GreenMarker } from '../../components/GreenMarker/GreenMarker';
import RadioInput from '../../components/RadioInput';
import { useCommonWidgetHandlers } from '../../helpers/widget-helpers/use-common-widget-handlers';
import { useGreenCueMarkers } from '../../hooks/use-green-cue-markers';
import { useFreeTrialUpgradeModal } from '../../modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import { EmailNotification } from './Email';
import { EmailToContactWidget } from './EmailToContactWidget';
import './EmailWidget.scss';
import { MobileNotification } from './Mobile';

export type EmailWidgetProps = {
  widget: WidgetModel;
  isExpanded?: boolean;
  draggableProvided?: DraggableProvided;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

export const EmailWidget = (props: EmailWidgetProps) => {
  const { isExpanded, widget, draggableProvided, platforms } = props;
  const { november2021Pricing } = useSelector(combinedFeatureFlagsSelector);

  const {
    config: { kind },
  } = widget as unknown as WidgetBase<EmailWidgetConfig>;

  const modalActions = useModalActions();
  const {
    notificationWidget: hasNotificaitonWidget,
    show_cues: showActivationCues,
  } = useSelector(combinedFeatureFlagsSelector);
  const [alertVisible, setAlertVisible] = useState(!hasNotificaitonWidget);
  const onCloseAlert = (e: IconClickEvent) => {
    e.stopPropagation();
    setAlertVisible(false);
  };
  const { isNotificationWidgetActivated } = useGreenCueMarkers();
  const { isFreeTrialPromotionWithoutSubscription, onAlertClick } =
    useFreeTrialUpgradeModal();

  const commonWidgetHandlers = useCommonWidgetHandlers();

  const [selectedNotificationType, setSelectedNotificationType] =
    useState<NotificationWidgetKind>(kind || 'email');

  const upateWidgetNotificationType = async (kind: NotificationWidgetKind) => {
    setSelectedNotificationType(kind);
    await commonWidgetHandlers.update({
      ...widget,
      config: { ...widget.config, kind },
    } as any);
    if (kind === 'email' && !isNotificationWidgetActivated) {
      setTimeout(
        () =>
          showActivationCues &&
          isFreeTrialPromotionWithoutSubscription &&
          modalActions.openFeatureActivationCongratsModal({
            feature: 'notification_email',
          }),
        1500,
      );
    }
  };

  const radioName = `${selectedNotificationType}-${widget.id}`;
  const widgetBody = () => {
    switch (selectedNotificationType) {
      case 'email':
        return <EmailNotification {...props} />;
      case 'mobile':
        return <MobileNotification {...props} />;
      case 'to_contact':
        return <EmailToContactWidget {...props} />;
      case 'mark_as_follow_up':
      default:
        return null;
    }
  };
  const widgetHeading = () => {
    if (!isExpanded || selectedNotificationType === 'to_contact') {
      return null;
    }
    return (
      <>
        <div className="notification-label notification-label--first">
          Notification Method
        </div>
        <div className="notification-selector">
          <RadioInput
            className="notification-type__input"
            name={radioName}
            checked={selectedNotificationType === 'mobile'}
            value="mobile"
            readOnly
            label={
              <span className="notification-type__mobile">
                <span>Push Notification</span>
                <span
                  className="notification-type__mobile--link"
                  onClick={() => {
                    window.open(
                      'https://customers.ai/help/article/ptx8i6csuz-mobile-monkey-push-notification',
                    );
                  }}
                >
                  <i className="far fa-question-circle" />
                </span>
              </span>
            }
            onClick={() => upateWidgetNotificationType('mobile')}
          />
          {!isNotificationWidgetActivated && (
            <GreenMarker className="email-widget__email-cue" />
          )}
          <RadioInput
            className="notification-type__input"
            name={radioName}
            checked={selectedNotificationType === 'email'}
            value="email"
            readOnly
            label="Email"
            onClick={() => upateWidgetNotificationType('email')}
          />
          <RadioInput
            className="notification-type__input"
            name={radioName}
            checked={selectedNotificationType === 'mark_as_follow_up'}
            value="mark_as_follow_up"
            readOnly
            label="Mark as follow up"
            onClick={() => upateWidgetNotificationType('mark_as_follow_up')}
          />
        </div>
      </>
    );
  };

  return (
    <div
      className={classnames('email-widget', 'email-widget--startbots-layout')}
    >
      <FacebookPageIcon />
      <div
        className={classnames(
          'email-widget__main-container',
          'email-widget--startbots-layout',
        )}
      >
        <div className="email-widget__content">
          {widgetHeading()}
          {widgetBody()}
          {alertVisible && (
            <Alert
              className="email-widget__trial-alert"
              type="warning"
              closeIconProps={{ size: 18, onClick: onCloseAlert }}
              onClick={() =>
                onAlertClick(
                  landmineNames.NOTIFICATION_WIDGET_TRIAL_ENDED_ALERT,
                )
              }
            >
              <div>
                <span>
                  You no longer have access to this{' '}
                  {november2021Pricing ? 'Startup ' : ' Operator'} feature. To
                  continue using the notification widget
                </span>{' '}
                <Link
                  href=""
                  className="email-widget__alert-action"
                  onClick={e => e.preventDefault()}
                >
                  upgrade to {november2021Pricing ? 'Startup ' : ' Operator'}.
                </Link>
              </div>
            </Alert>
          )}
        </div>
        <WidgetControls
          widget={widget}
          draggableProvided={draggableProvided}
          platforms={platforms}
        />
      </div>
    </div>
  );
};
